import { Delete as DeleteIcon, History as HistoryIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../contexts';
import { useDebounce } from '../../hooks/useDebounce';
import { DELETE_HOURLY_ACCOUNT_USER_V2, LIST_HOURLY_ACCOUNT_USERS_V2 } from '../../queries';
import { datagridFilterPanelAttributes } from '../../utils/cypress';
import { AuditHistoryModal } from '../audit/auditHistoryModal';
import { DatagridLinearProgress } from '../loading/datagridLinearProgress';
import { ConfirmationInputModal } from '../modals/confirmationInputModal';

const defaultSortOrder = {
	sort: 'desc',
	field: 'pid',
};

const PortalUsersToolbar = () => (
	<GridToolbarContainer>
		<GridToolbarFilterButton data-cy='filter-table-button' />
		<GridToolbarColumnsButton />
		<GridToolbarDensitySelector />
	</GridToolbarContainer>
);

export const PortalUsersTable = () => {
	const { organizationId, setShowDialog, setShowSnackbar, apolloHeaders } = useContext(CommonContext);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState<number>(0);
	const [rows, setRows] = useState([]);
	const [sortOrder, setSortOrder] = useState(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const {
		data: hourlyAccountUsersData,
		loading: hourlyAccountUsersLoading,
		refetch: hourlyAccountUsersRefetch,
	} = useQuery(LIST_HOURLY_ACCOUNT_USERS_V2, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			filters,
			order: sortOrder,
			organizationId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
		fetchPolicy: 'network-only',
		ssr: false,
	});

	const [deleteHourlyAccountUser] = useMutation(DELETE_HOURLY_ACCOUNT_USER_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	useEffect(() => {
		if (!hourlyAccountUsersLoading && hourlyAccountUsersData) {
			setRows(hourlyAccountUsersData?.listHourlyAccountUsers?.hourlyAccountUsers);
			setRowCount(hourlyAccountUsersData?.listHourlyAccountUsers?.count);
		}
	}, [hourlyAccountUsersLoading, hourlyAccountUsersData]);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			debouncedSetFilters(
				filterModel.items?.map((filterItem) => ({
					propertyName: filterItem.field,
					op: filterItem.operator,
					value: filterItem.value,
				})),
			);
		},
		[debouncedSetFilters],
	);

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			if (!sortModel.length) {
				return setSortOrder(defaultSortOrder);
			}

			const { field, sort } = sortModel[0];
			return setSortOrder({ field, sort });
		},
		[setSortOrder],
	);

	const handleDeleteAccountUser = useCallback(
		(accountUser) => {
			const userName = `${accountUser.user.firstName} ${accountUser.user.lastName}`;
			const confirmationInputModal = (
				<ConfirmationInputModal
					title={`Are you sure you want to delete the user ${userName}`}
					validationString='DELETE'
					dismissText='Cancel'
					confirmText='Confirm'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={() =>
						deleteHourlyAccountUser({
							variables: {
								id: accountUser.id,
							},
							onCompleted: () => {
								setShowDialog(null);
								setShowSnackbar({
									message: `Hourly account user deleted successfully`,
									severity: 'success',
								});
								hourlyAccountUsersRefetch();
							},
							onError: (apolloError) => {
								setShowSnackbar({
									message: apolloError.message,
									severity: 'error',
								});
							},
						})
					}
				/>
			);
			setShowDialog(confirmationInputModal);
		},
		[deleteHourlyAccountUser, hourlyAccountUsersRefetch, setShowDialog, setShowSnackbar],
	);

	const handleAuditHistory = useCallback(
		(accountUser) => {
			const dialog = (
				<AuditHistoryModal
					title='Hourly Portal User Audit History'
					resourceId={accountUser.id}
					resourceType={ResourceTypes.HOURLY_ACCOUNT_USER}
					onClose={() => setShowDialog(null)}
				/>
			);

			setShowDialog(dialog);
		},
		[setShowDialog],
	);

	const columns: GridColDef<any>[] = [
		{
			field: 'actions',
			headerName: 'Actions',
			width: 100,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				const actions = (
					<>
						<IconButton onClick={() => handleDeleteAccountUser(params.row)}>
							<DeleteIcon />
						</IconButton>
						<IconButton onClick={() => handleAuditHistory(params.row)}>
							<HistoryIcon />
						</IconButton>
					</>
				);

				return actions;
			},
		},
		{
			field: 'pid',
			headerName: 'Portal Account ID',
			width: 150,
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			renderCell: (params) => (
				<a
					href={`/portal-details/?accountId=${params.row.hourlyAccount.id}`}
					target='_blank'
					rel='noreferrer'
					style={{ color: 'blue' }}
				>
					PID-{params.row.hourlyAccount.pid}
				</a>
			),
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility #',
			width: 150,
			valueGetter: (params) => params.row.hourlyAccount.facility.facilityNumber,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			width: 300,
			valueGetter: (params) => params.row.hourlyAccount.facility.facilityName,
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			width: 150,
			valueGetter: (params) => params.row.user.lastName,
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			width: 150,
			valueGetter: (params) => params.row.user.firstName,
		},
		{
			field: 'email',
			headerName: 'Email Address',
			width: 250,
			valueGetter: (params) => params.row.user.email,
		},
		{
			field: 'active',
			type: 'boolean',
			headerName: 'Active',
			width: 150,
			sortable: false,
			filterable: false,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			width: 100,
			filterable: false,
			sortable: false,
			valueGetter: (params) => params.row.hourlyAccount.organizationSettings.name,
		},
	];

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='portals-datagrid-container'>
			<DataGridPro
				sx={{ width: 'auto', height: '100%', minHeight: '200px' }}
				rows={rows || []}
				rowCount={rowCount}
				columns={columns}
				loading={hourlyAccountUsersLoading}
				pagination
				pageSizeOptions={[pageSize]}
				paginationMode='server'
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={(newModel) => {
					setPaginationModel(newModel);
				}}
				sortingMode='server'
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				slots={{
					toolbar: PortalUsersToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					filterPanel: {
						sx: {
							'& .MuiDataGrid-filterFormValueInput': { width: 240 },
						},
						...datagridFilterPanelAttributes,
					},
				}}
			/>
		</div>
	);
};
